import React, { useEffect, useRef, useState } from 'react'

import '../../containers/mainSections/administrationTabs/devices.css';
import ConfirmModal from '../confirmModal';
import { naturalSort } from '../../utils/generalFunctions';

const DevicesStep = ({ devices, userSchedules, userUpdate, updateSchedule }) => {
    const [filterText, setFilterText] = useState(''); // Texto del filtro
    const [filterOption, setFilterOption] = useState('all'); // Filtro avanzado (pares, impares, activos, inactivos)
    const [order, setOrder] = useState('init'); // Orden actual: 'init', 'asc', 'desc'
    const [orderBy, setOrderBy] = useState(null); // Columna para ordenar
    const [deviceList, setDeviceList] = useState(() => structuredClone(devices)); // Lista de dispositivos
    const [scheduleList, setScheduleList] = useState(() => structuredClone(userSchedules)); // Lista de userSchedules
    const [isModified, setIsModified] = useState(false); // Nuevo estado para rastrear modificaciones

    const [selectedDevices, setSelectedDevices] = useState([]); // Dispositivos seleccionados
    const headerCheckboxRef = useRef(null); // Referencia para el checkbox del encabezado

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingScheduleChange, setPendingScheduleChange] = useState(null);

    const handleFilter = (e) => setFilterText(e.target.value.toLowerCase());

    const handleSelectChange = (e) => setFilterOption(e.target.value);

    const handleSort = (column) => {
        const newOrder = orderBy === column ? (order === 'init' ? 'asc' : (order === 'asc' ? 'desc' : 'init')) : 'asc';

        setOrder(newOrder);
        setOrderBy(column);

        if (newOrder === 'init') {
            setDeviceList(devices);
            return;
        } else {
            const isAsc = newOrder === 'asc';

            const sortedDevices = [...deviceList].sort((a, b) => {
                if (column === 'label' || column === 'schedule') {
                    const valueA = column === 'label' ? a.label : userSchedules.find(schedule => schedule.vehicleId === a.id)?.name || 'No asignado';
                    const valueB = column === 'label' ? b.label : userSchedules.find(schedule => schedule.vehicleId === b.id)?.name || 'No asignado';

                    // Extraer números de los valores
                    const numA = parseInt(valueA.match(/\d+/)?.[0] || NaN, 10);
                    const numB = parseInt(valueB.match(/\d+/)?.[0] || NaN, 10);

                    if (isNaN(numA) && isNaN(numB)) {
                        // Orden alfabético si ambos valores no tienen números
                        return isAsc ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
                    } else if (isNaN(numA)) {
                        // Los valores sin número se colocan después
                        return isAsc ? 1 : -1;
                    } else if (isNaN(numB)) {
                        // Los valores sin número se colocan después
                        return isAsc ? -1 : 1;
                    } else {
                        // Orden numérico si ambos valores tienen números
                        return isAsc ? numA - numB : numB - numA;
                    }
                } else if (column === 'status') {
                    return isAsc ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
                } else {
                    return 0; // Default, no se aplica ordenación
                }
            });

            setDeviceList(sortedDevices);
        }
    };

    const getSortIcon = (column) => {
        if (orderBy !== column || order === 'init') return <span>-</span>;
        return order === 'asc' ? (
            <span className='material-symbols-rounded'>arrow_upward_alt</span>
        ) : (
            <span className='material-symbols-rounded'>arrow_downward_alt</span>
        );
    };

    const toggleDeviceStatus = (index) => {
        const deviceId = deviceList[index]?.id;
        const updatedDeviceList = deviceList.map(device =>
            device.id === deviceId
                ? { ...device, status: device.status === 'activo' ? 'inactivo' : 'activo' }
                : device
        );
        setDeviceList(updatedDeviceList);

        const isModifiedNow = updatedDeviceList.some(device => {
            const original = devices.find(d => d.id === device.id);
            return original?.status !== device.status;
        });

        setIsModified(isModifiedNow);
    };

    const handleScheduleChange = (index, newScheduleName) => {
        const deviceId = deviceList[index]?.id;

        if (newScheduleName === "No asignado") {
            applyScheduleChange(deviceId, newScheduleName);
            return;
        }

        const scheduleIndex = scheduleList.findIndex(schedule => schedule.name === newScheduleName);

        if (scheduleIndex !== -1) {
            const existingVehicleId = scheduleList[scheduleIndex]?.vehicleId;

            if (existingVehicleId && existingVehicleId !== deviceId) {
                setPendingScheduleChange({ deviceId, newScheduleName });
                setIsConfirmModalOpen(true);
            } else {
                applyScheduleChange(deviceId, newScheduleName);
            }
        } else {
            console.error(`No se encontró un horario con el nombre: ${newScheduleName}`);
        }
    };

    const applyScheduleChange = (deviceId, newScheduleName) => {
        const updatedScheduleList = [...scheduleList];

        if (newScheduleName === "No asignado") {
            const scheduleIndex = updatedScheduleList.findIndex(schedule => schedule.vehicleId === deviceId);
            if (scheduleIndex !== -1) {
                updatedScheduleList[scheduleIndex].vehicleId = null;
            }
        } else {
            const scheduleIndex = updatedScheduleList.findIndex(schedule => schedule.name === newScheduleName);
            const existingScheduleIndex = updatedScheduleList.findIndex(
                schedule => schedule.vehicleId === deviceId && schedule.name !== newScheduleName
            );

            if (existingScheduleIndex !== -1) {
                updatedScheduleList[existingScheduleIndex].vehicleId = null;
            }

            updatedScheduleList[scheduleIndex].vehicleId = deviceId;
        }

        setScheduleList(updatedScheduleList);

        const isModifiedNow = updatedScheduleList.some((schedule, i) =>
            schedule.vehicleId !== userSchedules[i]?.vehicleId
        );
        setIsModified(isModifiedNow);
    };

    const applyFilters = () => {
        let filteredDevices = devices;

        // Filtrar por texto
        if (filterText) {
            filteredDevices = filteredDevices.filter((device) =>
                device.label.toLowerCase().includes(filterText)
            );
        }

        // Filtrar por opción avanzada
        switch (filterOption) {
            case 'even': // IDs pares
                filteredDevices = filteredDevices.filter((_, index) => (index + 1) % 2 === 0);
                break;
            case 'odd': // IDs impares
                filteredDevices = filteredDevices.filter((_, index) => (index + 1) % 2 !== 0);
                break;
            case 'active': // Solo activos
                filteredDevices = filteredDevices.filter((device) => device.status === 'activo');
                break;
            case 'inactive': // Solo inactivos
                filteredDevices = filteredDevices.filter((device) => device.status === 'inactivo');
                break;
            default:
                break;
        }

        setDeviceList(filteredDevices);
    };

    useEffect(() => {
        applyFilters();
    }, [filterText, filterOption, devices]);

    const handleCheckboxChange = (index) => {
        setSelectedDevices((prevSelected) =>
            prevSelected.includes(index)
                ? prevSelected.filter((i) => i !== index)
                : [...prevSelected, index]
        );
    };

    const toggleSelectedStatus = () => {
        if (selectedDevices.length === 0) return;

        // Verificar si todos los seleccionados tienen el mismo estado
        const firstDeviceStatus = deviceList[selectedDevices[0]].status;
        const allSameStatus = selectedDevices.every(index => deviceList[index].status === firstDeviceStatus);

        if (allSameStatus) {
            // Invertir el estado si todos tienen el mismo
            const updatedDevices = deviceList.map((device, index) =>
                selectedDevices.includes(index)
                    ? { ...device, status: device.status === 'activo' ? 'inactivo' : 'activo' }
                    : device
            );

            setDeviceList(updatedDevices);
            setIsModified(true); // Indicar que se realizaron modificaciones
            return;
        }

        // Contar los dispositivos activos e inactivos entre los seleccionados
        let activeCount = 0;
        let inactiveCount = 0;

        selectedDevices.forEach(index => {
            if (deviceList[index].status === 'activo') {
                activeCount++;
            } else {
                inactiveCount++;
            }
        });

        // Determinar el nuevo estado basado en el conteo
        const newStatus = activeCount >= inactiveCount ? 'activo' : 'inactivo';

        // Actualizar el estado de los dispositivos seleccionados
        const updatedDevices = deviceList.map((device, index) =>
            selectedDevices.includes(index)
                ? { ...device, status: newStatus }
                : device
        );

        setDeviceList(updatedDevices);
        setIsModified(true); // Indicar que se realizaron modificaciones
    };



    const saveChanges = () => {
        // Crear una copia de los dispositivos originales
        const updatedDevices = devices.map((device, index) => {
            const modifiedDevice = deviceList.find((d) => d.label === device.label);
            return modifiedDevice || device; // Si el dispositivo fue modificado, usarlo; si no, conservar el original
        });

        // Actualizar los dispositivos en el backend
        userUpdate({ vehicles: updatedDevices });

        const allSchedules = userSchedules.map((schedule, index) => {
            const modifiedSchedule = scheduleList.find((s) => s.name === schedule.name);
            return modifiedSchedule || null; // Si el horario fue modificado, usarlo; si no, conservar el original
        });

        // Filtrar y conservar solo los horarios modificados
        const updatedSchedules = scheduleList.filter((schedule) => {
            const originalSchedule = userSchedules.find((s) => s.name === schedule.name);
            return (
                !originalSchedule || // Si no se encuentra en los originales, es nuevo o modificado
                originalSchedule.vehicleId !== schedule.vehicleId || // Comparar cambios en vehicleId
                JSON.stringify(originalSchedule.turns) !== JSON.stringify(schedule.turns) // Comparar cambios en turns
            );
        });

        // Llamar al método para actualizar horarios en el backend
        if (updatedSchedules.length > 0) {
            // console.log("Horarios modificados:", updatedSchedules);
            updateSchedule(allSchedules, updatedSchedules);
        } else {
            console.log("No hay cambios en los horarios.");
        }

        // Restablecer estado de modificación
        setIsModified(false);
    };

    // Actualizar el estado "indeterminado" del checkbox del encabezado
    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate =
                selectedDevices.length > 0 && selectedDevices.length < deviceList.length;
        }
    }, [selectedDevices, deviceList.length]);

    const handleRowClick = (index) => {
        handleCheckboxChange(index);
    };

    const handleHeaderClick = () => {
        if (selectedDevices.length === deviceList.length) {
            setSelectedDevices([]); // Desmarcar todos
        } else {
            setSelectedDevices(deviceList.map((_, i) => i)); // Seleccionar todos
        }
    };

    return (
        <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', overflowY: 'hidden', height: '100%', width: '100%' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                Asigne los horarios creados a sus flotas vehiculares y modifique su estatus entre "Activo" e "Inactivo" para definir aquellos que se encuentran en ruta.
            </h3>
            <div className='card_content' style={{ display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
                <div className='actions_container'>
                    <div className='filters_container'>
                        <span>Filtrar por nombre:</span>
                        <input
                            type="text"
                            placeholder="Buscar dispositivo..."
                            value={filterText}
                            onChange={handleFilter}
                            className="filter_input"
                        />
                        <span>Mostrar:</span>
                        <select value={filterOption} onChange={handleSelectChange} className="filter_select">
                            <option value="all">Todos</option>
                            <option value="even">Solo pares</option>
                            <option value="odd">Solo impares</option>
                            <option value="active">Solo activos</option>
                            <option value="inactive">Solo inactivos</option>
                        </select>
                    </div>
                    <div className='buttons_container'>
                        <button className='change_status_btn' disabled={selectedDevices.length === 0} onClick={toggleSelectedStatus}>Cambiar estatus</button>
                        <button className='save_changes_btn' disabled={!isModified} onClick={saveChanges}>Guardar Cambios</button>
                    </div>
                </div>
                <ConfirmModal title={'Confirmación'} text={'Este horario ya está asignado a otro vehículo. ¿Desea asignarlo a este dispositivo de todas formas?'} isOpen={isConfirmModalOpen} onClose={setIsConfirmModalOpen}
                    confirmFunction={() => {
                        if (pendingScheduleChange) {
                            applyScheduleChange(
                                pendingScheduleChange.deviceId,
                                pendingScheduleChange.newScheduleName
                            );
                        }
                        setIsConfirmModalOpen(false);
                    }} />
                <div className='table_wrapper'>
                    <table className="devices_table">
                        <thead>
                            <tr>
                                <th className='checkbox_cell' onClick={handleHeaderClick}>
                                    <input
                                        type="checkbox"
                                        ref={headerCheckboxRef}
                                        onChange={(e) =>
                                            setSelectedDevices(
                                                e.target.checked
                                                    ? deviceList.map((_, i) => i)
                                                    : []
                                            )
                                        }
                                        checked={
                                            selectedDevices.length === deviceList.length && deviceList.length > 0
                                        }
                                        onClick={(e) => e.stopPropagation()} // Evitar doble activación
                                    />
                                </th>
                                <th onClick={() => handleSort('label')}>
                                    <span>Vehículo</span> {getSortIcon('label')}
                                </th>
                                <th onClick={() => handleSort('status')}>
                                    <span>Estatus</span> {getSortIcon('status')}
                                </th>
                                <th onClick={() => handleSort('schedule')}>
                                    <span>Horario</span> {getSortIcon('schedule')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {deviceList.map((device, index) => {
                                const assignedSchedule = scheduleList.find(schedule => schedule.vehicleId === device.id);
                                const schdeuleValue = assignedSchedule === null || assignedSchedule === undefined ? 'No asignado' : assignedSchedule.name;

                                return (
                                    <tr key={index}>
                                        <td
                                            className='checkbox_cell'
                                            onClick={() => handleRowClick(index)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedDevices.includes(index)}
                                                onChange={() => handleCheckboxChange(index)}
                                                onClick={(e) => e.stopPropagation()} // Evitar doble activación
                                            />
                                        </td>
                                        <td style={{ cursor: 'pointer' }} onClick={() => handleRowClick(index)}>{device.label}</td>
                                        <td className='checkbox_status' onClick={() => toggleDeviceStatus(index)}>
                                            <span className={device.status === 'activo' ? 'success' : 'danger'} >{device.status === 'activo' ? ' Activo' : ' Inactivo'}</span>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={device.status === 'activo' ? true : false}
                                                    onChange={() => toggleDeviceStatus(index)}
                                                    onClick={(e) => e.stopPropagation()} // Evitar doble activación
                                                />
                                            </label>
                                        </td>
                                        <td>
                                            <select
                                                id={'device ' + index}
                                                value={schdeuleValue}
                                                onChange={(e) => handleScheduleChange(index, e.target.value)}
                                            >
                                                <option value="No asignado">No asignado</option>
                                                {scheduleList
                                                    .slice()
                                                    .sort(naturalSort)
                                                    .map((schedule) => (
                                                        <option key={schedule.name} value={schedule.name}>
                                                            {schedule.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </td>
                                    </tr>
                                )
                            }
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default DevicesStep